// Global Instructions Rule Applied!
//
// Consolidated-fund detail screen (opened from the Consolidated Funds grid
// as a form-type tab). The tab set MIRRORS the fund full-form (owner
// ruling 2026-08-08: same navigation as a normal fund wherever it makes
// sense): Dashboard, Investments, Subscriptions (the group's "Investors"
// surface — subscriptions + the separate Allocate action), Capital Calls
// (one tab, fund-form section layout: Position → Capital Calls →
// Intercompany Transfers → shared Equalization section pointed at the
// primary fund), Distributions (consolidated distributions, waterfall
// phase 5: one lead row on the primary fund + one row per legal entity,
// edited in the standard Distribution form), Ledger and Statements (the SHARED fund-form tabs in
// consolidated mode: workpaper trial balance across member funds with
// intercompany eliminations; statements + subscription-level roll-forwards
// — the same components the fund form renders, EqualizationSection
// precedent). Per-fund-only tabs (Terms, Fee Booking, Chart of Accounts)
// stay on the member funds. Background tab panes stay mounted per house
// behaviour, so each tab owns its own data fetching.

import React, { useMemo } from 'react'
import { Tabs, Typography } from 'antd'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../state_manager/store.ts'
import { FundGroupDashboard } from './FundGroupDashboard.tsx'
import { FundGroupSubscriptionsTable } from './FundGroupSubscriptionsTable.tsx'
import { FundGroupCallsTab } from './FundGroupCallsTab.tsx'
import { FundGroupDistributionsTab } from './FundGroupDistributionsTab.tsx'
import { InvestmentsForm } from '../../investments/components/InvestmentsForm.tsx'
import LedgerTab from '../../funds/components/forms/tabs/LedgerTab.tsx'
import StatementsTab from '../../funds/components/forms/tabs/StatementsTab.tsx'

const { Title } = Typography

interface Props {
  recordId: string | null
  isNew?: boolean
}

export function FundGroupPage({ recordId }: Props): React.ReactElement {
  const fundGroups = useSelector((s: RootState) => s.globalConfigs?.fund_groups ?? [])
  const group = useMemo(
    () => fundGroups.find((g: { id: string }) => g.id === recordId) ?? null,
    [fundGroups, recordId]
  )
  const memberFundIds: string[] = group?.fund_ids ?? []
  const primaryFundId = group?.primary_fund_id ?? null
  // ONE object for both shared accounting tabs, kept across renders. Each
  // tab keys its first read on the identity of its `group` prop, so an
  // object built inline would make the tab read again on every render of
  // this form and put the default read (no FY, no as-of date) under
  // selectors that still show the user's choice.
  const accountingGroup = useMemo(
    () => (recordId ? { fundGroupId: recordId, primaryFundId } : null),
    [recordId, primaryFundId]
  )

  return (
    <div className='h-full overflow-y-auto bg-gray-50 p-3'>
      <div className='mb-2'>
        <Title level={5} style={{ margin: 0 }}>
          {group?.name ?? 'Consolidated Fund'}
        </Title>
      </div>
      <Tabs
        defaultActiveKey='dashboard'
        items={[
          {
            key: 'dashboard',
            label: 'Dashboard',
            children: <FundGroupDashboard fundGroupId={recordId} />
          },
          {
            key: 'investments',
            label: 'Investments',
            children: (
              <div style={{ height: 'calc(100vh - 220px)' }}>
                <InvestmentsForm presetFundIds={memberFundIds} />
              </div>
            )
          },
          {
            key: 'subscriptions',
            label: 'Investors',
            children: (
              <div style={{ height: 'calc(100vh - 220px)' }}>
                <FundGroupSubscriptionsTable fundGroupId={recordId} />
              </div>
            )
          },
          {
            key: 'capital-calls',
            label: 'Capital Calls',
            children: (
              <FundGroupCallsTab fundGroupId={recordId} primaryFundId={primaryFundId} />
            )
          },
          {
            key: 'distributions',
            label: 'Distributions',
            children: (
              <FundGroupDistributionsTab fundGroupId={recordId} primaryFundId={primaryFundId} />
            )
          },
          // shared fund-form accounting tabs in consolidated mode (fund
          // form's tab order: ledger before statements)
          ...(accountingGroup
            ? [
                {
                  key: 'ledger',
                  label: 'Ledger',
                  children: (
                    <div className='h-full'>
                      <LedgerTab fundId={null} group={accountingGroup} />
                    </div>
                  )
                },
                {
                  key: 'statements',
                  label: 'Statements',
                  children: (
                    <div className='h-full'>
                      <StatementsTab fundId={null} group={accountingGroup} />
                    </div>
                  )
                }
              ]
            : [])
        ]}
      />
    </div>
  )
}

export default FundGroupPage
